import {CommonModule, CurrencyPipe, DatePipe} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatChipsModule} from '@angular/material/chips';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Router} from '@angular/router';
import {lastValueFrom} from 'rxjs';
import {ContractList} from '../models/ContractList';
import {ContractListService} from './contract-list.service';

@Component({
  selector: 'app-contract-list',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatTooltipModule, MatChipsModule],
  templateUrl: './contract-list.component.html',
  styleUrls: ['./contract-list.component.css']
})
export class ContractListComponent implements OnInit {
  public contracts: ContractList[] = [];

  constructor(
    private service: ContractListService,
    private router: Router,
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      this.service.showSpinner();
      this.contracts = await lastValueFrom(this.service.getContractList());
    } catch (e) {
      console.error(e);
    } finally {
      this.service.hideSpinner();
    }
  }

  public get activeContractsCount(): number {
    return this.contracts.filter((contract) => this.isActive(contract)).length;
  }

  public get hasContracts(): boolean {
    return this.contracts.length > 0;
  }

  public get summaryText(): string {
    const count = this.activeContractsCount;
    return count === 1
      ? 'Você possui 1 contrato ativo.'
      : `Você possui ${count} contratos ativos.`;
  }

  public async returnToDashboard(): Promise<void> {
    await this.router.navigate(['principal/dashboard']);
  }

  public async openContract(contractId: number): Promise<void> {
    await this.router.navigate([`principal/contrato/${contractId}`]);
  }

  public contactSupport(): void {
    this.service.toasterInfo('Entre em contato com a sua unidade para obter suporte.');
  }

  public trackByContract(_: number, contract: ContractList): number {
    return contract.GEN_CONTRATO;
  }

  public isActive(contract: ContractList): boolean {
    return !contract.FINALIZAR_CONTRATO_DATA && !contract.DATA_FINALIZAR;
  }

  public statusLabel(contract: ContractList): string {
    return this.isActive(contract) ? 'Ativo' : 'Finalizado';
  }

  public statusIcon(contract: ContractList): string {
    return this.isActive(contract) ? 'check_circle' : 'task_alt';
  }

  public unitName(contract: ContractList): string {
    return contract.DESCRICAO_LOJA || 'Unidade não informada';
  }

  public unitSubtitle(): string {
    return 'Unidade vinculada ao contrato';
  }

  public boxName(contract: ContractList): string {
    return contract.DESCRICAO_BOX || contract.DESCRICAO || 'Box não informado';
  }

  public boxSubtitle(contract: ContractList): string {
    if (contract.AREA) {
      return `Box ${contract.AREA.toFixed(0).replace('.', ',')}m²`;
    }

    return 'Box contratado';
  }

  public formatDate(value: Date | string | null | undefined): string {
    if (!value) {
      return '—';
    }

    return this.datePipe.transform(value, 'dd/MM/yyyy') ?? '—';
  }

  public formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '—';
    }

    return this.currencyPipe.transform(value, 'BRL', 'symbol', '1.2-2', 'pt-BR') ?? '—';
  }

  public contractAgeText(contract: ContractList): string {
    return this.relativeLabel(contract.DATA_LOCACAO, 'past');
  }

  public finalizedText(contract: ContractList): string {
    if (this.isActive(contract)) {
      return 'Contrato ativo';
    }

    return this.relativeLabel(contract.FINALIZAR_CONTRATO_DATA || contract.DATA_FINALIZAR, 'past');
  }

  public nextPaymentDate(contract: ContractList): string {
    if (!contract.VENCIMENTO_PROXIMA_FATURA) {
      return this.isActive(contract) ? 'Não disponível' : 'Encerrado';
    }

    return this.formatDate(contract.VENCIMENTO_PROXIMA_FATURA);
  }

  public nextPaymentText(contract: ContractList): string {
    if (!contract.VENCIMENTO_PROXIMA_FATURA) {
      return this.isActive(contract) ? 'Sem cobrança futura disponível' : 'Sem novos vencimentos';
    }

    return this.relativeLabel(contract.VENCIMENTO_PROXIMA_FATURA, 'future');
  }

  public nextPaymentTone(contract: ContractList): 'neutral' | 'info' | 'warning' | 'danger' {
    if (!contract.VENCIMENTO_PROXIMA_FATURA || !this.isActive(contract)) {
      return 'neutral';
    }

    const diffDays = this.dayDifference(contract.VENCIMENTO_PROXIMA_FATURA);
    if (diffDays === null) {
      return 'neutral';
    }

    if (diffDays < 0) {
      return 'danger';
    }

    if (diffDays <= 5) {
      return 'warning';
    }

    return 'info';
  }

  private relativeLabel(value: Date | string | null | undefined, mode: 'past' | 'future'): string {
    const diffDays = this.dayDifference(value);
    if (diffDays === null) {
      return '—';
    }

    if (diffDays === 0) {
      return mode === 'future' ? 'Vence hoje' : 'Hoje';
    }

    if (diffDays > 0) {
      return mode === 'future' ? `Em ${diffDays} dia${diffDays > 1 ? 's' : ''}` : `Há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
    }

    const delay = Math.abs(diffDays);
    return mode === 'future' ? `${delay} dia${delay > 1 ? 's' : ''} em atraso` : `Há ${delay} dia${delay > 1 ? 's' : ''}`;
  }

  private dayDifference(value: Date | string | null | undefined): number | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    const baseDate = new Date();
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const today = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate()).getTime();

    return Math.round((target - today) / 86400000);
  }
}
